/**
 * UwAlternativeDialog - propose an E7 alternative offer: pick one of the released report's remaining
 * offers (rank order), add a Bemerkung, deliver via portal or e-mail with a Frist (P2 Slice 14 D4).
 */

import { useEffect, useState } from 'react';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import { ANTRAG_KEIN_PORTAL_HINWEIS, UW_ALTERNATIVE_TEXT_DEFAULT, UW_KEINE_ALTERNATIVEN_HINWEIS, type UnderwritingResponse, type UwAlternativeBody } from '@brokly/shared';
import { chf } from '../../lib/format';

const inputCls = 'w-full rounded-md border bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const ERROR_TEXT: Record<string, string> = {
  versand_unterbrochen: 'Versand unterbrochen - bitte zuerst aufloesen',
  offer_not_alternative: 'Diese Offerte steht nicht als Alternative zur Verfuegung',
  runde_offen: 'Es ist bereits eine Freigabe beim Kunden offen',
  frist_invalid: 'Die Frist liegt in der Vergangenheit',
  antrag_status_invalid: 'Der Antrag ist nicht mehr in diesem Status',
  kunde_email_fehlt: 'Beim Kunden ist keine E-Mail-Adresse hinterlegt',
  communication_send_required: 'Fuer den E-Mail-Versand fehlt die Berechtigung',
  mailbox_send_failed: 'Der E-Mail-Versand ist fehlgeschlagen',
};

export function UwAlternativeDialog({
  open,
  onClose,
  facts,
  onSubmit,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  facts: UnderwritingResponse;
  onSubmit: (body: UwAlternativeBody) => void;
  saving: boolean;
  error: string | null;
}) {
  const alternativen = facts.alternativen;
  const [offerId, setOfferId] = useState<string | null>(alternativen[0]?.offer_id ?? null);
  const [bemerkung, setBemerkung] = useState('');
  const [channel, setChannel] = useState<'portal' | 'email'>(facts.kanal_vorschlag);
  const [frist, setFrist] = useState(facts.frist_vorschlag);

  useEffect(() => {
    if (open) {
      const first = alternativen[0] ?? null;
      setOfferId(first?.offer_id ?? null);
      setBemerkung(first ? UW_ALTERNATIVE_TEXT_DEFAULT(first.carrier_name ?? first.carrier_uid, first.rang) : '');
      setChannel(facts.kanal_vorschlag);
      setFrist(facts.frist_vorschlag);
    }
  }, [open, alternativen, facts.kanal_vorschlag, facts.frist_vorschlag]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Alternative vorschlagen"
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Zurueck</Button>
          <Button onClick={() => offerId && onSubmit({ offer_id: offerId, channel, frist_date: frist, bemerkung: bemerkung.trim() || null })} disabled={saving || !offerId} data-testid="uw-alternative-submit">
            Alternative vorschlagen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="uw-alternative-dialog">
        {alternativen.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="uw-alternative-empty">{UW_KEINE_ALTERNATIVEN_HINWEIS}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th></th><th>Versicherer</th><th>Rang</th><th>Score</th><th>Ampel</th><th>Praemie</th><th>Beginn</th>
                </tr>
              </thead>
              <tbody>
                {alternativen.map((o) => (
                  <tr key={o.offer_id} data-testid="uw-alternative-row">
                    <td><input type="radio" name="uw-alt" checked={offerId === o.offer_id} onChange={() => { setOfferId(o.offer_id); setBemerkung(UW_ALTERNATIVE_TEXT_DEFAULT(o.carrier_name ?? o.carrier_uid, o.rang)); }} /></td>
                    <td>{o.carrier_name ?? o.carrier_uid}</td>
                    <td>{o.rang}</td>
                    <td>{o.score_total.toFixed(1)}</td>
                    <td>{o.ampel}</td>
                    <td>{o.praemie_jahr != null ? chf(o.praemie_jahr) : '-'}</td>
                    <td>{o.beginn ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <label className="text-sm">
          Bemerkung an den Kunden
          <textarea value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} rows={3} className={inputCls} data-testid="uw-alternative-bemerkung" />
        </label>
        <fieldset className="text-sm">
          <legend className="text-muted-foreground">Kanal</legend>
          <label className="mr-4"><input type="radio" name="uw-alt-kanal" checked={channel === 'portal'} disabled={!facts.portal_verfuegbar} onChange={() => setChannel('portal')} /> Kundenportal</label>
          <label><input type="radio" name="uw-alt-kanal" checked={channel === 'email'} disabled={!facts.kunde_email} onChange={() => setChannel('email')} /> E-Mail an {facts.kunde_email ?? '-'}</label>
          {!facts.portal_verfuegbar && <p className="text-xs text-muted-foreground">{ANTRAG_KEIN_PORTAL_HINWEIS}</p>}
        </fieldset>
        <label className="text-sm">
          Frist fuer die Freigabe
          <input type="date" value={frist} onChange={(e) => setFrist(e.target.value)} className={inputCls} data-testid="uw-alternative-frist" />
        </label>
        {error && <p role="alert" className="text-sm text-status-danger" data-testid="uw-alternative-error">{ERROR_TEXT[error] ?? error}</p>}
      </div>
    </Dialog>
  );
}
